import {
  nestByStoryID,
  rollupTimeSeriesBar,
  demographicsBar,
  campaignSummaryData,
  rollupContentHubOverview,
  rollupContentHubDetails,
} from "../components/report/D3Functions";
import drillEvents from "../components/report/utils/drillEvents";
import calculateCTAClicks from "../components/report/utils/ctaClick";

// Extract daily deal metadata
const extractDailyDealMetadata = (story) => {
  const metadata = {
    title: "tbd, creative fallback",
    image: "n/a",
    description: "n/a",
  };

  if (story.name) {
    metadata.title = story.name;
  }

  if (Array.isArray(story.images) && story.images.length > 0) {
    metadata.image = {
      orig_src: story.images[0],
      src: story.images[0],
    };
  } else if (Array.isArray(story.creatives) && story.creatives.length > 0) {
    const selectedCreative = story.creatives[0];
    metadata.image = {
      orig_src: selectedCreative.image,
      src: selectedCreative.image,
    };
  }

  if (story.description) {
    metadata.description = story.description;
  } else if (Array.isArray(story.creatives) && story.creatives.length > 0) {
    const selectedCreative = story.creatives[0];
    metadata.description = selectedCreative.lead;
  }

  return metadata;
};

// Find A/B test for story and locale
const findAbTest = (abTests, storyID, locale) => {
  let abTest = Array.isArray(abTests)
    ? abTests.find((storyAbTest) => storyAbTest.storyID === storyID)
    : null;

  if (!abTest) {
    return;
  }

  let variantsByLanguage = [];
  if (abTest?.variants) {
    variantsByLanguage = abTest.variants[locale];
  }

  return { ...abTest, variants: variantsByLanguage };
};

// Find story in GA summarized data
const findStoryInGaSummarized = (gaSummarized, storyID, locale) => {
  const metrics = gaSummarized?.metrics;
  if (!metrics || !metrics.stories) {
    return undefined;
  }

  const storyByID = metrics.stories[storyID];

  if (!storyByID || !(locale in storyByID)) {
    return undefined;
  }

  return storyByID[locale];
};

// Check if a language has an active line item.
// To determine an actively running story, you need to check line_items for:
// - lang_code
// - published_from / published_until
// - status: "ready"
const hasActiveLineItem = (story, locale) => {
  if (!Array.isArray(story.line_items) || story.line_items.length === 0) {
    return false;
  }
  return story.line_items.some(
    (lineItem) =>
      lineItem.lang_code === locale && lineItem.status === "ready",
  );
};

// Generate language-specific story objects
const generateLanguageStories = (
  stories,
  projectData,
  abTests,
  gaSummarized,
  rows,
) => {
  const { isDailyDeal } = projectData;
  const storiesByLanguage = [];

  if (Array.isArray(stories)) {
    stories.forEach((story) => {
      if (Array.isArray(story.languages)) {
        // Filter languages to only include those with active line items
        const activeLanguages = story.languages.filter((lang) =>
          hasActiveLineItem(story, lang.locale),
        );

        activeLanguages.forEach((lang) => {
          let abTest = findAbTest(abTests, story.id, lang.locale);
          let gaSummarizedData = findStoryInGaSummarized(
            gaSummarized,
            story.id,
            lang.locale,
          );

          const extractedDailyDealMetadata = isDailyDeal
            ? extractDailyDealMetadata(story, lang.locale)
            : null;

          const og_image =
            story.metadata?.og_image?.[lang.locale] ||
            (isDailyDeal && extractedDailyDealMetadata?.image) ||
            null;

          const metadataLanguage = {
            og_title: {
              [lang.locale]:
                story.metadata?.og_title?.[lang.locale] ||
                (isDailyDeal && extractedDailyDealMetadata?.title) ||
                null,
            },
            og_description: {
              [lang.locale]:
                story.metadata?.og_description?.[lang.locale] ||
                (isDailyDeal && extractedDailyDealMetadata?.description) ||
                null,
            },
            og_image: {
              [lang.locale]: og_image ? { ...og_image } : null,
            },
          };

          const linksLanguage = {};
          if (story.links) {
            Object.keys(story.links).forEach((p) => {
              if (story.links[p][lang.locale]) {
                linksLanguage[p] = {
                  [lang.locale]: story.links[p][lang.locale],
                };
              }
            });
          }

          const storyByLanguage = {
            ...story,
            id: `${story.id}-${lang.locale}`,
            name:
              story.metadata?.og_title?.[lang.locale] ||
              metadataLanguage?.og_title?.[lang.locale] ||
              story.name ||
              null,
            metadata: metadataLanguage,
            language: lang.locale,
            creativesWithDFP: Array.isArray(story.creativesWithDFP)
              ? story.creativesWithDFP.filter(
                  (c) => c.language_code === lang.locale,
                )
              : [],
            languages: Array.isArray(story.languages)
              ? story.languages.filter((c) => c.locale === lang.locale)
              : [],
            links: linksLanguage,
            abTest,
            gaSummarized: gaSummarizedData,
          };

          // Enhance with KPIs
          const enhancedStory = enhanceStoryWithKPIs(storyByLanguage, rows);
          storiesByLanguage.push(enhancedStory);
        });
      }
    });
  }

  return storiesByLanguage.length > 0 ? storiesByLanguage : stories;
};

const generateOutboundClicksPerURL = (events) => {
  const outboundClicksStartWithOutboundLink = events.filter(
    (e) =>
      (e.eventName === "click" && Boolean(e.outbound)) ||
      e.eventName.startsWith("outboundlink_"),
  );
  const outboundClicksWithoutCTA = events.filter(
    (e) =>
      e.eventName === "outbound_click" && !e?.element_type?.includes("CTA"),
  );
  const outboundClicks = outboundClicksStartWithOutboundLink
    .concat(outboundClicksWithoutCTA)
    .reduce((p, e) => {
      let linkUrl = e.outbound_url === "(not set)" ? "" : e.outbound_url;

      // fallback to e.linkUrl
      if (!linkUrl && e.linkUrl) {
        linkUrl = e.linkUrl;
      }

      if (!linkUrl) {
        return p;
      }

      if (!p[linkUrl]) {
        p[linkUrl] = 0;
      }

      p[linkUrl] += parseInt(e.eventCount);
      return p;
    }, {});
  return Object.entries(outboundClicks)
    .map(([url, clickCount]) => ({
      url,
      clickCount,
    }))
    .toSorted((a, b) => b.clickCount - a.clickCount);
};

const generateCtaClickList = (events) => {
  const eventsToFilter = Array.isArray(events) ? events : [];

  const ctaCollection = [];
  const ctaUnique = new Set();
  const ctaSummedUp = [];

  const filtered = eventsToFilter.filter((e) => e.key === "ctaClick");

  if (Array.isArray(filtered)) {
    filtered.forEach((e) => {
      const raw = e.value && e.value.raw ? e.value.raw : [];

      if (Array.isArray(raw)) {
        raw.forEach((r) => {
          ctaCollection.push(r);
          ctaUnique.add(r.eventName);
        });
      }
    });
  }

  const ctaUniqueArray = Array.from(ctaUnique);

  ctaUniqueArray.forEach((l) => {
    const foundCTAs = ctaCollection.filter((f) => l === f.eventName);

    let eventCount = 0;

    foundCTAs.forEach((f) => {
      eventCount += Number(f.eventCount);
    });

    const obj = {
      ctaCategory: l,
      eventCount,
    };

    ctaSummedUp.push(obj);
  });

  return ctaSummedUp;
};

const filterCtaClickListGroupByElementName = (events) => {
  const groupedCTA = events
    .filter(
      (e) =>
        e.eventName === "outbound_click" && e?.element_type?.includes("CTA"),
    )
    .reduce((result, item) => {
      const { element_name } = item;
      let eventCount = parseInt(item.eventCount, 10);

      if (isNaN(eventCount)) {
        eventCount = 0;
      }

      if (!result[element_name]) {
        result[element_name] = { element_name, eventCount: 0 };
      }

      result[element_name].eventCount += eventCount;

      return result;
    }, {});

  return Object.values(groupedCTA);
};

const sumDFPMetrics = (creativesWithDFP, metricName) => {
  let total = 0;

  creativesWithDFP.forEach((c) => {
    if (c[metricName]) {
      total += c[metricName];
    }
  });

  return total;
};

const avgScrollDepth = (events) => {
  if (!Array.isArray(events)) {
    return 0;
  }

  const pageViews =
    parseInt(events.find((e) => e.eventName === "page_view")?.eventCount, 10) ||
    0;

  if (pageViews === 0) {
    return 0;
  }

  const eventCount_scroll_100 =
    parseInt(
      events.find(
        (e) => e.eventName === "scroll" && e.percentScrolled === "100",
      )?.eventCount,
      10,
    ) || 0;
  const eventCount_scroll_end =
    parseInt(
      events.find((e) => e.eventName === "scroll_end")?.eventCount,
      10,
    ) || 0;
  const eventCount_scroll_75 =
    parseInt(
      events.find((e) => e.eventName === "scroll" && e.percentScrolled === "75")
        ?.eventCount,
      10,
    ) || 0;
  const eventCount_scroll_50 =
    parseInt(
      events.find((e) => e.eventName === "scroll" && e.percentScrolled === "50")
        ?.eventCount,
      10,
    ) || 0;
  const eventCount_scroll_25 =
    parseInt(
      events.find((e) => e.eventName === "scroll" && e.percentScrolled === "25")
        ?.eventCount,
      10,
    ) || 0;

  const scroll_100 = 100 * eventCount_scroll_100;
  const scroll_90 =
    90 * Math.abs(eventCount_scroll_end - eventCount_scroll_100);
  const scroll_75 = 75 * Math.abs(eventCount_scroll_75 - eventCount_scroll_end);
  const scroll_50 = 50 * Math.abs(eventCount_scroll_50 - eventCount_scroll_75);
  const scroll_25 = 25 * Math.abs(eventCount_scroll_25 - eventCount_scroll_50);

  return (
    (scroll_100 + scroll_90 + scroll_75 + scroll_50 + scroll_25) / pageViews
  );
};

// Transform story KPIs
const transformStoryKPIs = (kpiFound) => {
  const outboundClicksWithOutboundLink = kpiFound.events
    .filter(
      (e) =>
        (e.eventName === "click" && Boolean(e.outbound)) ||
        e.eventName.startsWith("outboundlink_"),
    )
    .reduce((p, e) => p + parseInt(e.eventCount), 0);
  const outboundClicksWithoutCTA = kpiFound.events
    .filter(
      (e) =>
        e.eventName === "outbound_click" && !e?.element_type?.includes("CTA"),
    )
    .reduce((p, e) => p + parseInt(e.eventCount), 0);

  const impressions = Array.isArray(kpiFound.creativesFiltered)
    ? sumDFPMetrics(kpiFound.creativesFiltered, "impressions")
    : undefined;
  const clicks = Array.isArray(kpiFound.creativesFiltered)
    ? sumDFPMetrics(kpiFound.creativesFiltered, "clicks")
    : undefined;

  return {
    // Preserve all original kpiFound.value properties first
    ...kpiFound.value,
    // Then add/override with calculated values
    impressions,
    clicks,
    ctr: Array.isArray(kpiFound.creativesFiltered)
      ? (sumDFPMetrics(kpiFound.creativesFiltered, "clicks") /
          sumDFPMetrics(kpiFound.creativesFiltered, "impressions")) *
        100
      : undefined,
    videoPlays:
      drillEvents(kpiFound.events, "video_play") +
      drillEvents(kpiFound.events, "video_start"),
    outboundClicks: outboundClicksWithoutCTA + outboundClicksWithOutboundLink,
    ctaClicks: calculateCTAClicks(kpiFound.events),
    shares:
      drillEvents(kpiFound.events, "share_start") +
      drillEvents(kpiFound.events, "open_share_options") +
      drillEvents(kpiFound.events, "begin_to_share") +
      drillEvents(kpiFound.events, "share"),
    formSubmissions: drillEvents(kpiFound.events, "form_submit"),
    scrollDepth: avgScrollDepth(kpiFound.events),
    outboundClicksPerURL: generateOutboundClicksPerURL(kpiFound.events),
    ctaClicksPerElementName: filterCtaClickListGroupByElementName(
      kpiFound.events,
    ),
    ctaClickList: generateCtaClickList(kpiFound.events),
  };
};

// Enhanced story processing
const enhanceStoryWithKPIs = (story, kpis) => {
  if (!Array.isArray(kpis) || kpis.length === 0) {
    return story;
  }

  // For language-specific stories, we need to match the full ID with language suffix
  const kpiFound = kpis.find((kpi) => {
    const kpiKey = String(kpi.key);
    const storyId = String(story.id);
    // Direct match or story ID is contained in the KPI key (for language variants)
    return kpiKey === storyId || kpiKey.startsWith(storyId + "-");
  });

  if (!kpiFound) {
    return story;
  }

  return {
    ...story,
    storyKPIs: transformStoryKPIs(kpiFound),
    // Enhance creativesWithDFP with CTR calculation
    creativesWithDFP:
      story.creativesWithDFP?.map((c) => ({
        ...c,
        ctr: c.impressions ? (c.clicks / c.impressions) * 100 : 0,
      })) || [],
  };
};

// TODO: move to backend
export const transformResponse = (data) => {
  const transformed = {
    raw: data,
    project: null,
    reportOverview: {},
    storySelection: [],
    storiesByLanguage: [],
    storyKPIs: [],
    campaignKPIs: {},
    timeSeriesBar: {},
    demographicsBar: {},
    contentHub: {
      overview: {},
      details: {},
    },
    dateRange: {
      from: null,
      to: null,
    },
  };

  // Project data processing
  if (data?.project) {
    transformed.project = {
      ...data.project,
      daterangeFrom: data.daterange?.from,
      daterangeTo: data.daterange?.to,
    };

    transformed.reportOverview = {
      name: data.project.name || undefined,
      client: data.project.client || undefined,
    };
  }

  // CRASS data processing
  if (data?.CRASS) {
    transformed.reportOverview.name = data.CRASS.name;
    if (!transformed.reportOverview.client) {
      transformed.reportOverview.client = data.CRASS.customer?.name || "n/a";
    }
  }

  // Content hub override
  if (data.project?.isContentHub) {
    transformed.reportOverview.name = data.project.name || "n/a";
    transformed.reportOverview.client = data.project.client || "n/a";
  }

  transformed.storySelection = getStorySelection(data);

  // GA Raw data processing
  if (data?.gaRaw) {
    let rows = nestByStoryID(data.gaRaw[0]?.data?.rowObjects || []);

    // Handle linkout campaigns
    if (
      data.project?.isLinkoutCampaign ||
      (data.project?.isDailyDeal &&
        rows.length === 0 &&
        Array.isArray(data.CRASS?.stories))
    ) {
      const rowsLinkout = [];
      data.CRASS.stories.forEach((e) => {
        const actualID = e.id;
        if (Array.isArray(e.languages)) {
          // Filter languages to only include those with active line items
          const activeLanguages = e.languages.filter((lang) =>
            hasActiveLineItem(e, lang.locale),
          );
          activeLanguages.forEach((l) => {
            rowsLinkout.push({ key: `${actualID}-${l.locale}` });
          });
        }
      });
      rows = rowsLinkout;
    }

    // Process events
    const eventData = data.gaRaw.find((e) => e.custom_name === "query_events");
    const events = eventData?.data?.rowObjects || [];

    // Enhanced row processing with DFP integration
    if (Array.isArray(rows) && transformed.storySelection.length > 0) {
      const storiesToUse = transformed.storySelection;

      rows.forEach((row) => {
        const storyId = String(row.key);
        const languagePattern = /(-)(\w{2})$/;
        const languageMatch = storyId.match(languagePattern);
        const storyLanguage = languageMatch?.[2];

        const story = storiesToUse.find(
          (s) => String(storyId) === String(`${s.id}-${storyLanguage}`),
        );

        if (story) {
          // Enhance story with project flags
          story.language = storyLanguage;
          story.isLinkoutCampaign = !!data.project?.isLinkoutCampaign;
          story.isDailyDeal = !!data.project?.isDailyDeal;
          story.isContentHub = !!data.project?.isContentHub;

          // DFP integration
          if (story.adserver_order_id && data.DFP?.lineItems) {
            row.adserver_order_id = story.adserver_order_id;
            row.lineItem = data.DFP.lineItems.find(
              (l) => l.orderId === story.adserver_order_id,
            );
            row.lineItemsFiltered = data.DFP.lineItems.filter(
              (l) => l.orderId === story.adserver_order_id,
            );
            row.creativesFiltered = data.DFP.creatives.filter((l) => {
              const crassCreative = story.creatives?.find(
                (c) => c.adserver_id === l.creativeId,
              );
              return (
                crassCreative &&
                l.orderId === story.adserver_order_id &&
                crassCreative.language_code === storyLanguage
              );
            });
          }

          // Process creatives with DFP
          if (Array.isArray(story.creatives) && data.DFP?.creatives) {
            story.creativesWithDFP = story.creatives.map((creative) => {
              const dfpCreatives = data.DFP.creatives.filter(
                (c) => creative.adserver_id === c.creativeId,
              );

              const totals = dfpCreatives.reduce(
                (acc, c) => ({
                  impressions: acc.impressions + (c.impressions || 0),
                  clicks: acc.clicks + (c.clicks || 0),
                }),
                { impressions: 0, clicks: 0 },
              );

              // Handle CRASS dates
              const datesCRASS = {
                published_from: null,
                published_until: null,
              };
              const lineItem = story.line_items?.find(
                (li) =>
                  creative.line_item_id === li.id &&
                  creative.line_item_adserver_id === li.adserver_id,
              );

              if (lineItem) {
                datesCRASS.published_from = lineItem.published_from;
                datesCRASS.published_until = lineItem.published_until;

                // Handle media title codes
                creative.media_title_codes = creative.media_title_codes.map(
                  (mt) => {
                    if (mt === "20minuten") {
                      const hasWettbewerb = lineItem.positions.some(
                        (p) =>
                          p.includes("wettbewerbe") ||
                          p.includes("concours-et-jeux"),
                      );
                      return hasWettbewerb ? `${mt}-wettbewerb` : `${mt}-front`;
                    }
                    return mt;
                  },
                );
              }

              return {
                ...creative,
                ...dfpCreatives[0],
                ...totals,
                ...datesCRASS,
              };
            });
          }
        }

        // Append events to rows
        row.events = events.filter((e) => row.key === e.storyID);
      });
    }

    // Filter rows with creatives
    rows = rows.filter((r) => r.creativesFiltered?.length > 0);

    // Enhance stories in storySelection with KPIs
    transformed.storySelection = transformed.storySelection.map((story) =>
      enhanceStoryWithKPIs(story, rows),
    );

    // Generate language-specific stories with all enhancements
    if (data.abTests || data.gaSummarized) {
      transformed.storiesByLanguage = generateLanguageStories(
        transformed.storySelection,
        data.project || {},
        data.abTests,
        data.gaSummarized,
        rows,
      );
    }

    transformed.storyKPIs = rows;

    // Generate derived data
    const idsToDisplay = rows.map((row) => row.key);
    transformed.timeSeriesBar = rollupTimeSeriesBar(
      data.gaRaw[0]?.data?.rowObjects || [],
      idsToDisplay,
    );

    // Demographics data
    const demographicsData = data.gaRaw.find(
      (e) => e.custom_name === "query_demographics",
    );
    transformed.demographicsBar = demographicsBar(
      demographicsData?.data?.rowObjects || [],
    );

    // Campaign summary
    if (
      !data.project?.isLinkoutCampaign &&
      (!data.project?.isDailyDeal || (data.project?.isDailyDeal && rows.length))
    ) {
      const summary = campaignSummaryData(rows);

      // Calculate DFP totals
      const dfpTotals = rows.reduce(
        (acc, row) => {
          if (Array.isArray(row.creativesFiltered)) {
            row.creativesFiltered.forEach((c) => {
              acc.impressions += c.impressions || 0;
              acc.clicks += c.clicks || 0;
            });
          }
          return acc;
        },
        { impressions: 0, clicks: 0 },
      );

      transformed.campaignKPIs = { ...summary, ...dfpTotals };
    }

    // Content Hub specific processing
    if (data.project?.isContentHub) {
      const contentHubData = data.gaRaw.find(
        (e) => e.custom_name === "query_contenthub_overview",
      );

      if (contentHubData?.data?.rowObjects) {
        transformed.contentHub.overview = rollupContentHubOverview(
          contentHubData.data.rowObjects,
        );
        transformed.contentHub.details = rollupContentHubDetails(
          contentHubData.data.rowObjects,
        );
      }
    }
  }

  // Date range
  if (data.daterange) {
    transformed.dateRange = {
      from: data.daterange.from,
      to: data.daterange.to,
    };
  }

  return transformed;
};

const getStorySelection = (data) => {
  const { project } = data;

  if (project?.projectType === "storyLinkChoice") {
    const titleData = data.gaRaw?.find((e) => e.custom_name === "query_title");
    return generateUrlStoryObjects(project, titleData);
  }

  if (project?.projectType === "abnumberChoice") {
    return getAbnumberStorySelection(data);
  }

  return [];
};

const getAbnumberStorySelection = (data) => {
  const { project, CRASS } = data;
  if (project.isContentHub) {
    return extractContentHubStories(CRASS?.campaigns);
  }
  if (project.isDailyDeal) {
    return transformDailyDealStories(CRASS?.stories);
  }
  return CRASS?.stories || [];
};

const extractContentHubStories = (campaigns) => {
  return (
    campaigns
      ?.filter((c) => Array.isArray(c.stories))
      .flatMap((c) => c.stories) || []
  );
};

const transformDailyDealStories = (stories) => {
  return (
    stories?.map((story) => ({
      ...story,
      ...(story.line_items?.length === 1 && {
        id: `daily_${story.line_items[0].id}`,
      }),
    })) || []
  );
};

const generateUrlStoryObjects = (projectData, titleData) => {
  const allStories = [];

  const titles =
    titleData && titleData.data && titleData.data.rowObjects
      ? titleData.data.rowObjects
      : [];

  if (Array.isArray(projectData.urls)) {
    projectData.urls.forEach((e) => {
      const id = e.replace("https://", "");
      let title = "n/a";
      const titleCandidates = titles.filter((t) => t.storyID === id);

      if (Array.isArray(titleCandidates)) {
        // for case multiple candidates
        const sorted = titleCandidates.sort(
          (a, b) =>
            parseFloat(b.screenPageViews) - parseFloat(a.screenPageViews),
        );

        if (sorted && sorted[0] && sorted[0].dimension2) {
          title = sorted[0].dimension2;
        }
      }

      const urlStory = {
        id,
        name: title,
        reportType: "URL",
      };

      // extract story infos from URL && append if existing
      const extractionPattern =
        /(cp\.)(.*)(\.ch\/)(\w{2})(\/stories\/)(\d{2,8})/i;
      const patternMatch = e.match(extractionPattern);

      if (Array.isArray(patternMatch) && patternMatch.length === 7) {
        // we have data to extract from URL
        urlStory.links = {};
        urlStory.links[patternMatch[2]] = {};
        urlStory.links[patternMatch[2]][patternMatch[4]] = e;
      }

      allStories.push(urlStory);
    });
  }

  return allStories;
};
